import React from 'react';
import { Users, Clock, TrendingUp, Map } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const EmployeeFlow: React.FC = () => {
  // Mock data - in real app this would come from sensors
  const flowData = {
    currentCount: 245,
    peakTime: '10:30 AM',
    avgDuration: '4.2h',
    activeAreas: ['Main Office', 'Conference Center', 'Innovation Lab'],
    trafficHotspots: [
      { location: 'Main Entrance', flow: 850 },
      { location: 'Cafeteria', flow: 620 },
      { location: 'Central Hub', flow: 450 },
      { location: 'East Wing', flow: 380 }
    ],
    hourlyFlow: [
      { time: '8AM', inflow: 85, outflow: 20 },
      { time: '9AM', inflow: 120, outflow: 45 },
      { time: '10AM', inflow: 95, outflow: 65 },
      { time: '11AM', inflow: 75, outflow: 85 },
      { time: '12PM', inflow: 110, outflow: 95 },
      { time: '1PM', inflow: 65, outflow: 110 },
      { time: '2PM', inflow: 45, outflow: 75 },
      { time: '3PM', inflow: 35, outflow: 90 },
      { time: '4PM', inflow: 25, outflow: 120 },
      { time: '5PM', inflow: 15, outflow: 150 }
    ]
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Employee Flow Analysis</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Users className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Current Count</h3>
          </div>
          <p className="text-3xl font-bold">{flowData.currentCount}</p>
          <p className="text-gray-500">people in building</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Peak Time</h3>
          </div>
          <p className="text-3xl font-bold">{flowData.peakTime}</p>
          <p className="text-gray-500">highest traffic</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <TrendingUp className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Avg Duration</h3>
          </div>
          <p className="text-3xl font-bold">{flowData.avgDuration}</p>
          <p className="text-gray-500">time in building</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Map className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Active Areas</h3>
          </div>
          <p className="text-3xl font-bold">{flowData.activeAreas.length}</p>
          <p className="text-gray-500">busy locations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Traffic Hotspots</h3>
          <div className="space-y-4">
            {flowData.trafficHotspots.map((spot, index) => (
              <div key={index} className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{spot.location}</p>
                  <p className="text-sm text-gray-500">{spot.flow} paths/day</p>
                </div>
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 rounded-full h-2"
                    style={{ width: `${(spot.flow / 1000) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Hourly Flow</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={flowData.hourlyFlow}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="inflow" fill="#82ca9d" name="Entering" />
              <Bar dataKey="outflow" fill="#8884d8" name="Exiting" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFlow; 